/**
 * «Logg inn med Vipps» for staff (owner, 2026-11-01) — the panel's half. The server's half and the security model:
 * apps/api/src/staff-vipps-login.js, docs/platform-implementation/STAFF_VIPPS_LOGIN.md.
 *
 *  - A login or a link starts with POST /admin/auth/vipps/start; the answer's one-time `binding` is kept in
 *    sessionStorage (this tab only, like the session) and the page goes to Vipps.
 *  - Vipps comes back to #/login?vippsCode=… (a login) or #/?vippsLink=… (a link), or with the reason it did not work
 *    (?vipps=… / ?vippsLinkError=…). The code leaves the address FIRST, then it is spent with the binding.
 *  - Without the binding the code is worth nothing: a code from somebody else's Vipps, opened in this browser, signs
 *    nobody in here (login CSRF), and a link is stored only for the account that started it, with its own session.
 */
import { API, post, type Sent } from './api';

const BIND_KEY = 'masjid.admin.vipps-bind:' + API;

export type VippsReturn =
  | { kind: 'login'; code: string } | { kind: 'loginError'; reason: string }
  | { kind: 'link'; code: string } | { kind: 'linkError'; reason: string };

/** What a failed Vipps sign-in says. «refused» and «failed» say the same: the panel does not tell an account's state. */
export const LOGIN_ERROR: Record<string, string> = {
  unknown: 'Denne Vipps-brukeren er ikke koblet til en ansatt her. Logg inn med e-post og passord og koble til Vipps under Min konto.',
  cancelled: 'Innloggingen med Vipps ble avbrutt.',
  refused: 'Kunne ikke logge inn med Vipps. Prøv igjen, eller logg inn med e-post og passord.',
  failed: 'Kunne ikke logge inn med Vipps. Prøv igjen, eller logg inn med e-post og passord.',
};
/** What a failed «Koble til Vipps» says. */
export const LINK_ERROR: Record<string, string> = {
  cancelled: 'Koblingen til Vipps ble avbrutt.',
  failed: 'Vipps kunne ikke bekrefte deg. Prøv igjen.',
};

/** Where Vipps sends this page back to: the panel itself (origin, path and query — the server allows only its own panel). */
const panelReturn = () => `${location.origin}${location.pathname}${location.search}`;

/** Read a return from Vipps out of the address and take it out (before anything is spent). Null when there is none. */
export function takeVippsReturn(): VippsReturn | null {
  const [route, query] = location.hash.replace(/^#/, '').split('?');
  const q = new URLSearchParams(query || '');
  let out: VippsReturn | null = null;
  if (q.get('vippsCode')) out = { kind: 'login', code: q.get('vippsCode') as string };
  else if (q.get('vipps')) out = { kind: 'loginError', reason: q.get('vipps') as string };
  else if (q.get('vippsLink')) out = { kind: 'link', code: q.get('vippsLink') as string };
  else if (q.get('vippsLinkError')) out = { kind: 'linkError', reason: q.get('vippsLinkError') as string };
  if (out) history.replaceState(null, '', `${location.pathname}${location.search}#${route === '/login' ? '/' : route || '/'}`);
  return out;
}

/** Whether this mosque offers «Logg inn med Vipps» (Vipps Login configured). False when it cannot be asked. */
export async function vippsOffered(): Promise<boolean> {
  try {
    const r = await fetch(API + '/admin/auth/methods', { cache: 'no-store' });
    return r.ok ? Boolean((await r.json()).vipps) : false;
  } catch { return false; }
}

/** Start a login or a link and go to Vipps. Returns an error text, or nothing (the page leaves). */
export async function startVipps(mode: 'login' | 'link'): Promise<string | null> {
  const r = await post('/auth/vipps/start', { mode, returnTo: panelReturn() });
  if (!r.ok) return r.body.message_no || r.body.message || 'Vipps kunne ikke startes. Prøv igjen.';
  try { sessionStorage.setItem(BIND_KEY, String(r.body.binding || '')); } catch { /* storage blocked: the exchange will be refused */ }
  location.assign(r.body.authorizeUrl);
  return null;
}

/** Spend a one-time code with this tab's binding (used once either way). */
export async function exchangeVipps(code: string): Promise<Sent> {
  let binding = '';
  try { binding = sessionStorage.getItem(BIND_KEY) || ''; sessionStorage.removeItem(BIND_KEY); } catch { /* storage blocked */ }
  return post('/auth/vipps/exchange', { code, binding });
}
